import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PromptStageMeta } from '../../constants/articlePromptStages';
import { PROMPT_DEFAULTS } from '../../constants/articlePromptDefaults';
import { validatePrompt, PromptWarning } from './promptValidation';
import { updatePrompt } from '../../services/promptService';

interface Props {
  stage: PromptStageMeta;
  type: 'system' | 'user' | 'block';
  promptId: string;
  initialContent: string;
  onSaved: () => void;
}

const PromptEditor: React.FC<Props> = ({ stage, type, promptId, initialContent, onSaved }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState(initialContent);
  const [warnings, setWarnings] = useState<PromptWarning[]>([]);
  const [saved, setSaved] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertPlaceholder = (name: string) => {
    const el = textareaRef.current;
    const token = `%${name}%`;
    if (!el) {
      setContent((c) => c + token);
      return;
    }
    const at = el.selectionStart ?? content.length;
    setContent(content.slice(0, at) + token + content.slice(el.selectionEnd ?? at));
  };

  const save = async () => {
    setWarnings(validatePrompt(content, stage, type));
    await updatePrompt(promptId, { content });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onSaved();
  };

  const restoreDefault = () => {
    setContent(PROMPT_DEFAULTS[`${stage.subFeature}:${type}`]);
    setWarnings([]);
  };

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase text-gray-500">
        {t(`articlePrompts.${type}_prompt`)}
      </div>
      <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={12}
        className="w-full p-3 border rounded-md font-mono text-xs"
      />
      <div className="flex flex-wrap gap-1">
        {stage.placeholders.map((p) => (
          <button
            key={p.name}
            type="button"
            title={p.description}
            onClick={() => insertPlaceholder(p.name)}
            className="px-2 py-0.5 border rounded-full text-xs font-mono bg-gray-50 hover:bg-blue-50"
          >
            %{p.name}%
          </button>
        ))}
      </div>
      {warnings.map((w) => (
        <div key={`${w.kind}-${w.placeholder}`} className="text-xs text-amber-700">
          {t(
            w.kind === 'missing_required'
              ? 'articlePrompts.warning_missing'
              : 'articlePrompts.warning_unknown',
            { placeholder: w.placeholder }
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-semibold"
        >
          {saved ? t('articlePrompts.saved') : t('articlePrompts.save')}
        </button>
        <button
          type="button"
          onClick={restoreDefault}
          className="px-3 py-1.5 border rounded-md text-sm"
        >
          {t('articlePrompts.restore_default')}
        </button>
      </div>
    </div>
  );
};

export default PromptEditor;
